import { Flex, Box, Text } from "@chakra-ui/layout";
import { Heading } from "@chakra-ui/react";
import { Row, Col } from "antd";
import NewTable from "components/NewTable";
import ContentItem from "./components/ContentItem";
import ListaArquivos from "components/ListaArquivos";
import { formatValuePercentage, formatMoney } from "infra/helpers/number";
import {
  mapTipoDocumentoToLabel,
  mapTipoEntidadeToLabel,
} from "components/Features/Financeiro/FormConta/util/helper";
import { OperacaoEnum } from "core/resources/enums/conta";
import useContentContaParcelasColumns from "./hooks/useContentContaParcelasColumns";
import moment from "moment";
import { ContaDetalheCompleteResponse } from "features/conta/typings";
import TableComposicaoConta from "./components/TableComposicaoConta/index";
import { mapFormaPagamentoToLabel } from "infra/helpers/mapFormaPagamentoToLabel";

interface ContentDetalheContaProps {
  data: ContaDetalheCompleteResponse;
  operacao?: OperacaoEnum;
}

function ContentDetalhesConta({ data }: ContentDetalheContaProps) {
  const columnsParcelas = useContentContaParcelasColumns();

  if (!data) {
    return null;
  }

  return (
    <>
      <Flex py="4px" as={Row} gutter={8}>
        <Flex as={Col} xs={6} flexDirection="column">
          <ContentItem titulo="RESUMO:" descricao={data.descricao || "--"} />
        </Flex>

        {data.unidade ? (
          <Flex as={Col} xs={6} flexDirection="column">
            <ContentItem titulo={"UNIDADE:"} descricao={data.unidade.nome} />
          </Flex>
        ) : null}

        <Flex as={Col} xs={data.unidade ? 6 : 12} flexDirection="column">
          <ContentItem
            titulo={`${mapTipoEntidadeToLabel(
              data.tipoEntidadeId,
              data.operacao
            ).toUpperCase()}:`}
            descricao={data.pessoa?.nome || "--"}
          />
        </Flex>
      </Flex>

      <Flex py="4px" as={Row} gutter={8}>
        <Flex as={Col} xs={6} flexDirection="column">
          <ContentItem
            titulo="VENCIMENTO:"
            descricao={
              data.dataVencimento
                ? moment(data.dataVencimento).format("DD/MM/YYYY")
                : "--"
            }
          />
        </Flex>

        <Flex as={Col} xs={6}>
          <ContentItem
            titulo="COMPETÊNCIA:"
            descricao={
              data.dataCompetencia
                ? moment(data.dataCompetencia, "YYYY-MM").format("MM/YYYY")
                : "--"
            }
          />
        </Flex>

        <Flex as={Col} xs={6} flexDirection="column">
          <ContentItem
            titulo="FORMA DE PAGAMENTO:"
            descricao={
              data.formaPagamentoId
                ? mapFormaPagamentoToLabel(data.formaPagamentoId)
                : "--"
            }
          />
        </Flex>

        <Flex as={Col} xs={6} flexDirection="column">
          <ContentItem
            titulo={
              data.operacao === OperacaoEnum.Despesa
                ? "CONTA DE PAGAMENTO:"
                : "CONTA DE RECEBIMENTO:"
            }
            descricao={data.bancoContaBancaria || "--"}
          />
        </Flex>
      </Flex>

      <Flex py="4px" as={Row} gutter={8}>
        <Flex as={Col} xs={6} flexDirection="column">
          <ContentItem
            titulo="DATA DE LIQUIDAÇÃO:"
            descricao={
              data.dataPagamento
                ? moment(data.dataPagamento).format("DD/MM/YYYY")
                : "--"
            }
          />
        </Flex>
        {data.operacao === OperacaoEnum.Receita ? (
          <Flex as={Col} xs={6} flexDirection="column">
            <ContentItem
              titulo="DATA DE CRÉDITO EM CONTA:"
              descricao={
                data.dataMovimentacao
                  ? moment(data.dataMovimentacao).format("DD/MM/YYYY")
                  : "--"
              }
            />
          </Flex>
        ) : null}

        <Flex as={Col} xs={6} flexDirection="column">
          <ContentItem titulo="ETIQUETA:" descricao={data.tag || "--"} />
        </Flex>

        {data.operacao === OperacaoEnum.Receita ? (
          <Flex as={Col} xs={6} flexDirection="column">
            <ContentItem
              titulo={"MÉTODOS DE COBRANÇA"}
              descricao={data.formasRecebimento
                .map(({ nome }) => nome)
                .join(", ")}
            />
          </Flex>
        ) : null}

        {data.operacao === OperacaoEnum.Despesa && data.pessoaPagador ? (
          <Flex as={Col} xs={6} flexDirection="column">
            <ContentItem
              titulo="Pagante:"
              descricao={data.pessoaPagador.nome || "--"}
            />
          </Flex>
        ) : null}
      </Flex>

      <Flex mb={4} justifyContent="space-between">
        <Heading size="md">Composição da conta</Heading>
        <Flex fontSize="12px">
          Valor Original:
          <Text fontSize="12px" fontWeight="bold" ml="2px">
            {formatMoney(data.valorInicial)}
          </Text>
        </Flex>
      </Flex>

      <TableComposicaoConta data={data} />

      <Flex mb={4} flexDir="column">
        {data.dataDocumento ||
        data.tipoDocumentoId ||
        data.numeroDocumento ||
        data.serieNota ? (
          <>
            <Flex>
              <Heading size="md">Documento fiscal</Heading>
            </Flex>

            <Flex py="4px" as={Row} gutter={8}>
              <Flex as={Col} xs={4}>
                <ContentItem
                  titulo="DATA DE EMISSÃO:"
                  descricao={
                    data.dataDocumento
                      ? moment(data.dataDocumento).format("DD/MM/YYYY")
                      : "--"
                  }
                />
              </Flex>

              <Flex as={Col} xs={6}>
                <ContentItem
                  titulo="TIPO:"
                  descricao={mapTipoDocumentoToLabel(data.tipoDocumentoId)}
                />
              </Flex>

              <Flex as={Col} xs={5}>
                <ContentItem
                  titulo="NÚMERO DA NOTA:"
                  descricao={data.numeroDocumento || "--"}
                />
              </Flex>

              <Flex as={Col} xs={5}>
                <ContentItem
                  titulo="SÉRIE DA NOTA:"
                  descricao={data.serieNota || "--"}
                />
              </Flex>
            </Flex>
          </>
        ) : null}
      </Flex>

      {data.imposto.issqn ||
      data.imposto.pisConfisCsll ||
      data.imposto.inss ||
      data.imposto.irrf ? (
        <>
          <Flex>
            <Heading mb={4} size="md">
              Impostos retidos
            </Heading>
          </Flex>

          <Flex justifyContent="space-between">
            <ContentItem
              titulo="ISSQN:"
              descricao={`${data.imposto.issqn || 0}% ${formatValuePercentage(
                data.imposto.issqn,
                data.valor
              )}`}
            />
            <ContentItem
              titulo="PIS, COFINS E CSLL:"
              descricao={`${
                data.imposto.pisConfisCsll || 0
              }% ${formatValuePercentage(
                data.imposto.pisConfisCsll,
                data.valor
              )}`}
            />
            <ContentItem
              titulo="INSS:"
              descricao={`${data.imposto.inss || 0}% ${formatValuePercentage(
                data.imposto.inss,
                data.valor
              )}`}
            />
            <ContentItem
              titulo="IRRF:"
              descricao={`${data.imposto.irrf || 0}% ${formatValuePercentage(
                data.imposto.irrf,
                data.valor
              )}`}
            />
          </Flex>
        </>
      ) : null}

      {data.parcelas?.length ? (
        <>
          <Flex>
            <Heading mb={4} size="md">
              Outras parcelas
            </Heading>
          </Flex>
          <Box as={Row}>
            <Box as={Col} xs={12}>
              <NewTable columns={columnsParcelas} dataSource={data.parcelas} />
            </Box>
          </Box>
        </>
      ) : null}

      {data.anexos?.length ? (
        <>
          <Flex>
            <Heading mb={4} size="md">
              Arquivos
            </Heading>
          </Flex>
          <ListaArquivos
            arquivos={
              data.anexos.map((anexo) => ({
                referencia: anexo.referencia,
                nome: anexo.nome,
                descricao: anexo.createdAt
                  ? `Anexo em ${moment(anexo.createdAt).format(
                      "DD/MM/YYYY HH:mm"
                    )}`
                  : undefined,
              })) || []
            }
          />
        </>
      ) : null}
    </>
  );
}

export default ContentDetalhesConta;
